import React, { useMemo, useState, useEffect, useRef } from "react";
import { useMeeting, useParticipant } from "../../config/sdkConfig";
import { nameTructed } from "../../utils/helper";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { CornerDisplayName } from "../../components/ParticipantView";
import SpeakerParticipantGrid from "../../components/SpeakerParticipantGrid";
import { useSiteContext } from "../../context/SiteContext";

// Component to manage pause/resume for participant streams based on focus
// Adaptive quality: <=10 participants = all HIGH, >10 = only active speaker HIGH
function SpeakerSideStreamController({ participantId, isActive, totalParticipants }) {
    const { pinnedParticipants, sustainedSpeakerId } = useMeeting();
    const { webcamStream, stopConsumingWebcamStreams, consumeWebcamStreams, setQuality, isActiveSpeaker } = useParticipant(participantId);

    // Check if this participant is pinned
    const isPinned = pinnedParticipants && (
      pinnedParticipants.has(participantId) ||
      pinnedParticipants.has(String(participantId)));

    const isSustainedSpeaker = sustainedSpeakerId &&
      String(participantId) === String(sustainedSpeakerId);

    useEffect(() => {
        if (webcamStream) {
            try {
                if (isPinned) {
                    setQuality('high');
                } else if (totalParticipants <= 10) {
                    setQuality('high');
                } else {
                    if (isActive || isActiveSpeaker || isSustainedSpeaker) {
                        setQuality('high');
                    } else {
                        setQuality('low');
                    }
                }
            } catch (error) {
                console.error(`Error controlling stream for ${participantId}:`, error);
            }
        }
    }, [isActive, webcamStream, participantId, totalParticipants, isActiveSpeaker, isPinned, isSustainedSpeaker]);

    return null;
}

function SpeakerSideParticipantView({ isPresenting }) {
    const {
        participants,
        pinnedParticipants,
        localParticipant,
        localScreenShareOn,
        presenterId,
        activeSpeakerId,
        instantSpeakerId,
        sustainedSpeakerId,
    } = useMeeting();
    const { hideSelfView } = useSiteContext();
    const { sideBarMode, setCurrentFocusParticipantId } =
        useMeetingAppContext();

    const [currentFocusParticipant, setCurrentFocusParticipant] = useState(null);
    const [speakerHistory, setSpeakerHistory] = useState([]);
    const speakerTimeoutRef = useRef(null);
    const currentFocusRef = useRef(null);
    const [mouseOver, setMouseOver] = useState(false);
    const [lastFocusParticipant, setLastFocusParticipant] = useState(null);

    // Helper function to get participant data from Map (handles string/number type coercion)
    const getParticipantData = (participantId) => {
        return participants.get(participantId) ||
               participants.get(Number(participantId)) ||
               participants.get(String(participantId));
    };

    // Helper function to log all participants
    const logAllParticipants = () => {
        console.log("=== All Participants ===");
        participants.forEach((participant, participantId) => {
            console.log(`ID: ${participantId}`, participant);
        });
        console.log("========================");
    };

    const isRecorderMode = new URLSearchParams(window.location.search).get('recorder') === 'true';

    const participantIds = useMemo(() => {
        if (!localParticipant) {
            if (isRecorderMode) return [...participants.keys()];
            return [];
        }

        const pinnedParticipantId = [...pinnedParticipants.keys()].filter(
            (participantId) => {
                return participantId !== localParticipant.id;
            }
        );
        const regularParticipantIds = [...participants.keys()].filter(
            (participantId) => {
                return (
                    ![...pinnedParticipants.keys()].includes(participantId) &&
                    localParticipant.id !== participantId
                );
            }
        );

        let ids = [
            localParticipant.id,
            ...pinnedParticipantId,
            ...regularParticipantIds,
        ];

        if (hideSelfView) {
            ids = ids.filter((id) => id !== localParticipant.id);
        }

        return ids;
    }, [
        participants,
        pinnedParticipants,
        presenterId,
        hideSelfView,
        localScreenShareOn,
        isRecorderMode,
    ]);

    // Prefer active speaker, then sustained, then any non-local, then local.
    useEffect(() => {
        console.log("participantIds", participantIds);
        logAllParticipants();
        if (participantIds.length > 0 && !currentFocusRef.current) {
            const localIdStr = String(localParticipant?.id);
            const activeStr = activeSpeakerId ? String(activeSpeakerId) : null;
            const sustainedStr = sustainedSpeakerId ? String(sustainedSpeakerId) : null;
            let chosen = null;
            if (activeStr && activeStr !== localIdStr) chosen = participantIds.find(id => String(id) === activeStr);
            if (!chosen && sustainedStr && sustainedStr !== localIdStr) chosen = participantIds.find(id => String(id) === sustainedStr);
            if (!chosen) chosen = participantIds.find(id => String(id) !== localIdStr);
            if (!chosen) chosen = participantIds[0];
            const participant = getParticipantData(chosen);
            const firstParticipantObj = {
                id: chosen,
                name:
                    participant?.displayName ||
                    participant?.name ||
                    `Participant ${String(chosen).slice(-4)}`,
                isLocal: chosen === localParticipant?.id,
            };
            setCurrentFocusParticipant(firstParticipantObj);
            setCurrentFocusParticipantId(chosen);
            currentFocusRef.current = firstParticipantObj;
        }
    }, [
        participantIds,
        participants,
        localParticipant,
        activeSpeakerId,
        sustainedSpeakerId,
        setCurrentFocusParticipantId,
    ]);

    useEffect(() => {
        if (
            hideSelfView &&
            currentFocusParticipant &&
            localParticipant &&
            currentFocusParticipant.id === localParticipant.id
        ) {
            console.log("Last focus participant:", lastFocusParticipant);
            if (lastFocusParticipant) {
                const participant = getParticipantData(lastFocusParticipant);
                setCurrentFocusParticipant({
                    id: lastFocusParticipant,
                    name:
                        participant?.displayName ||
                        participant?.name ||
                        `Participant ${String(lastFocusParticipant).slice(-4)}`,
                    isLocal: lastFocusParticipant === localParticipant?.id,
                });
                // Update context so sidebar ParticipantView can filter it out
                setCurrentFocusParticipantId(lastFocusParticipant);
                currentFocusRef.current = lastFocusParticipant;
            } else {
                const nextParticipant = participantIds.find(
                    (id) => id !== localParticipant?.id
                );
                if (nextParticipant) {
                    const participant = getParticipantData(nextParticipant);
                    setCurrentFocusParticipant({
                        id: nextParticipant,
                        name:
                            participant?.displayName ||
                            participant?.name ||
                            `Participant ${String(nextParticipant).slice(-4)}`,
                        isLocal: false,
                    });
                    // Update context so sidebar ParticipantView can filter it out
                    setCurrentFocusParticipantId(nextParticipant);
                    currentFocusRef.current = nextParticipant;
                }
            }
        }
    }, [
        hideSelfView,
        participantIds,
        participants,
        localParticipant,
        setCurrentFocusParticipantId,
    ]);

    // Track sustained speaker (4s rule) and rotate focus.
    // Pinned participants override sustained speaker — they always stay in focus.
    useEffect(() => {
        // If someone is pinned, they always get focus (overrides sustained speaker)
        const pinnedIds = [...pinnedParticipants.keys()].filter(id => id !== localParticipant?.id);
        if (pinnedIds.length > 0) {
            const pinnedId = pinnedIds[0];
            if (String(pinnedId) !== String(currentFocusRef.current?.id)) {
                const participant = getParticipantData(pinnedId);
                if (participant) {
                    const pinFocus = {
                        id: pinnedId,
                        name: participant.displayName || participant.name || `Participant ${String(pinnedId).slice(-4)}`,
                        isLocal: String(pinnedId) === String(localParticipant?.id),
                        timestamp: Date.now()
                    };
                    setCurrentFocusParticipant(pinFocus);
                    setCurrentFocusParticipantId(pinnedId);
                    currentFocusRef.current = pinFocus;
                }
            }
            return;
        }

        // Follow active speaker (600ms debounce) for responsive focus switching.
        if (activeSpeakerId && String(activeSpeakerId) !== String(currentFocusRef.current?.id)) {
            if (hideSelfView && localParticipant && String(activeSpeakerId) === String(localParticipant.id)) {
                return;
            }
            const participant = getParticipantData(activeSpeakerId);
            if (participant) {
                const newFocusParticipant = {
                    id: activeSpeakerId,
                    name: participant.displayName || participant.name || `Participant ${String(activeSpeakerId).slice(-4)}`,
                    isLocal: activeSpeakerId === localParticipant?.id,
                    timestamp: Date.now()
                };
                if (String(currentFocusRef.current?.id) !== String(lastFocusParticipant)) {
                    setLastFocusParticipant(currentFocusRef.current?.id);
                }
                setCurrentFocusParticipant(newFocusParticipant);
                setCurrentFocusParticipantId(activeSpeakerId);
                currentFocusRef.current = newFocusParticipant;
            }
        }
    }, [
        activeSpeakerId,
        pinnedParticipants,
        participantIds,
        participants,
        localParticipant,
        lastFocusParticipant,
        hideSelfView,
        setCurrentFocusParticipantId,
    ]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (speakerTimeoutRef.current) {
                clearTimeout(speakerTimeoutRef.current);
            }
        };
    }, []);
    return (
        <div className={`flex bg-gray-900 relative rounded-xl overflow-hidden w-full h-full`}
            style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}
        >
            {/* Render stream controllers for all participants - pause streams not in focus */}
            {participantIds.map((participantId) => {
                const isActive = currentFocusParticipant?.id === participantId;
                return (
                    <SpeakerSideStreamController
                        key={`speaker_side_stream_controller_${participantId}`}
                        participantId={participantId}
                        isActive={isActive}
                        totalParticipants={participantIds.length}
                    />
                );
            })}
            {/* Main Focus Area */}
            <div className="flex-1">
                {currentFocusParticipant ? (
                    <div
                        className="relative w-full h-full"
                        onMouseEnter={() => setMouseOver(true)}
                        onMouseLeave={() => setMouseOver(false)}
                    >
                        {/* Main Speaker Video - Full Screen */}
                        <div className={`w-full h-full bg-gray-900 relative overflow-hidden rounded-xl video-cover ${String(instantSpeakerId) === String(currentFocusParticipant.id) ? "ring-[3px] ring-green-500 ring-offset-2 ring-offset-gray-900 active-speaker-glow" : ""}`} style={{ boxShadow: String(instantSpeakerId) === String(currentFocusParticipant.id) ? "0 0 30px rgba(34, 197, 94, 0.6), 0 0 60px rgba(34, 197, 94, 0.3), 0 10px 40px rgba(0,0,0,0.3)" : "0 10px 40px rgba(0,0,0,0.3)", transition: "box-shadow 0.3s ease, ring 0.3s ease", border: String(instantSpeakerId) === String(currentFocusParticipant.id) ? "2px solid #22c55e" : "2px solid transparent" }}>
                            <FocusParticipantVideo
                                participantId={currentFocusParticipant.id}
                                isMainSpeaker={true}
                            />
                        </div>

                        {/* Corner Display Name. Live lookup from participants
                            Map at render time — currentFocusParticipant.name is
                            captured in useState and can hold a stale
                            "Participant <uid>" placeholder. See
                            FocusParticipantView for full bug context. */}
                        <FocusParticipantCornerDisplay
                            participantId={currentFocusParticipant.id}
                            displayName={
                                getParticipantData(currentFocusParticipant.id)?.displayName
                                || currentFocusParticipant.name
                            }
                            isLocal={currentFocusParticipant.isLocal}
                            mouseOver={mouseOver}
                            isActiveSpeaker={true}
                        />
                    </div>

                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center text-gray-400">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-700 flex items-center justify-center">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                                </svg>
                            </div>
                            <p className="text-sm">Waiting for participants to join</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Speaker History Sidebar */}
            {speakerHistory.length > 0 && (
                <div className="absolute right-12 top-12 w-48 bg-black bg-opacity-70 rounded-lg p-4">
                    <h4 className="text-white text-sm font-medium mb-2">Recent Speakers</h4>
                    <div className="space-y-2">
                        {speakerHistory.slice(0, 5).map((speaker, index) => (
                            <div
                                key={speaker.id}
                                className={`flex items-center gap-2 text-xs ${index === 0 ? 'text-green-400' : 'text-gray-300'
                                    }`}
                            >
                                <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-green-400' : 'bg-gray-400'
                                    }`}></div>
                                <span>{nameTructed(speaker.name, 15)}</span>
                                {speaker.isLocal && <span className="text-gray-500">(You)</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Focus Participant Corner Display Component
const FocusParticipantCornerDisplay = ({ participantId, displayName, isLocal, mouseOver, isActiveSpeaker }) => {
    const { micOn, webcamOn } = useParticipant(participantId);

    return (
        <CornerDisplayName
            participantId={participantId}
            isPresenting={false}
            displayName={displayName}
            isLocal={isLocal}
            micOn={micOn}
            webcamOn={webcamOn}
            mouseOver={mouseOver}
            isActiveSpeaker={isActiveSpeaker}
        />
    );
};

// Individual Focus Participant Video Component - memoized to prevent blinking
const FocusParticipantVideo = React.memo(({ participantId, isMainSpeaker }) => {
    const { webcamStream, micStream, webcamOn, micOn, displayName, isLocal, metaData } = useParticipant(participantId);
    const mirrorStyle = { transform: 'scaleX(-1)', WebkitTransform: 'scaleX(-1)' };
    const videoRef = useRef(null);
    const micRef = useRef(null);
    const prevTrackRef = useRef(null);

    useEffect(() => {
        if (webcamStream && videoRef.current) {
            // Only recreate MediaStream if the actual track changed
            if (prevTrackRef.current === webcamStream.track) {
                return;
            }
            prevTrackRef.current = webcamStream.track;
            const mediaStream = new MediaStream();
            mediaStream.addTrack(webcamStream.track);
            videoRef.current.srcObject = mediaStream;
            videoRef.current.play().catch(() => {});
        } else if (!webcamStream && videoRef.current) {
            videoRef.current.srcObject = null;
            prevTrackRef.current = null;
        }
    }, [webcamStream]);

    useEffect(() => {
        if (micRef.current) {
            // Remote audio is played by Agora's audioTrack.play() in AgoraContext.js
            if (isLocal && micOn && micStream) {
                const mediaStream = new MediaStream();
                mediaStream.addTrack(micStream.track);
                micRef.current.srcObject = mediaStream;
                micRef.current
                    .play()
                    .catch((error) =>
                        console.error("micRef.current.play() failed", error)
                    );
            } else {
                micRef.current.srcObject = null;
            }
        }
    }, [micStream, micOn, isLocal]);

    const isVideoVisible = webcamOn && webcamStream;

    return (
        <div className="relative w-full h-full">
            {/* Audio Stream */}
            <audio
                ref={micRef}
                autoPlay
                muted={isLocal}
                style={{ display: 'none' }}
            />
            {/* Video element always mounted to prevent blinking on re-renders */}
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={isLocal}
                className="w-full h-full object-cover"
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    ...mirrorStyle,
                    display: isVideoVisible ? "block" : "none"
                }}
            />
            {/* Avatar shown only when video is off */}
            {!isVideoVisible && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <div
                        className="flex items-center justify-center rounded-full 2xl:h-[92px] h-[52px] 2xl:w-[92px] w-[52px]"
                        style={{ background: "linear-gradient(135deg, #6a1fbf, #00c853)" }}
                    >
                        {metaData?.profileImage ? (
                            <img
                                alt="Profile"
                                className="w-full h-full rounded-full object-cover"
                                src={metaData?.profileImage}
                            />
                        ) : (
                            <span className="text-2xl font-bold text-white">
                                {displayName?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    return prevProps.participantId === nextProps.participantId;
});

const MemorizedParticipantView = React.memo(
    SpeakerSideParticipantView,
    (prevProps, nextProps) => {
        return prevProps.isPresenting === nextProps.isPresenting;
    }
);

export default MemorizedParticipantView;
